// src/components/admin/FeatureManagement.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { useApiCall } from '../../hooks/useApiCall';
import apiService from '../../services/apiService';
import './Admin.css';

const CATEGORIES = ['beta', 'billing', 'compliance', 'platform'];

const CATEGORY_COLORS = {
  beta:       'bg-purple-100 text-purple-700',
  billing:    'bg-green-100 text-green-700',
  compliance: 'bg-yellow-100 text-yellow-700',
  platform:   'bg-blue-100 text-blue-700',
};

// ─── Main Component ──────────────────────────────────────────────────────────

const FeatureManagement = ({ onBack }) => {
  const { user } = useAuth();
  const { loading, error, makeRequest } = useApiCall();

  const [features, setFeatures] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);

  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = async () => {
    try {
      const data = await makeRequest(
        () => apiService.get('/api/v1/features/registry', user),
        'Loading features...'
      );
      if (data.success) {
        setFeatures(data.features || []);
      }
    } catch (err) {
      console.error('Error loading features:', err);
    }
  };

  const createFeature = async (formData) => {
    const data = await makeRequest(
      () => apiService.post('/api/v1/features/registry', formData, user),
      'Creating feature...'
    );
    if (data.success) {
      setShowAddModal(false);
      await loadFeatures();
    }
  };

  const updateFeature = async (key, formData) => {
    const data = await makeRequest(
      () => apiService.put(`/api/v1/features/registry/${key}`, formData, user),
      'Updating feature...'
    );
    if (data.success) {
      setEditingFeature(null);
      await loadFeatures();
    }
  };

  const deactivateFeature = async (key) => {
    const data = await makeRequest(
      () => apiService.delete(`/api/v1/features/registry/${key}`, user),
      'Deactivating feature...'
    );
    if (data.success) {
      setConfirmDeactivate(null);
      await loadFeatures();
    }
  };

  const reactivateFeature = async (key) => {
    await updateFeature(key, { is_active: true });
  };

  const filtered = features.filter(f => {
    if (!showInactive && !f.is_active) return false;
    if (categoryFilter !== 'all' && f.category !== categoryFilter) return false;
    return true;
  });

  return (
    <div className="admin-page">
      {/* Header */}
      <div className="flex flex-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="btn btn-secondary btn-sm">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h2 className="text-2xl font-semibold text-gray-800">Feature Management</h2>
        </div>
        <div className="flex gap-2">
          <button onClick={loadFeatures} className="btn btn-secondary" disabled={loading}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button onClick={() => setShowAddModal(true)} className="btn btn-primary" disabled={loading}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Feature
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex gap-2">
          {['all', ...CATEGORIES].map(cat => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              className={`btn btn-sm ${categoryFilter === cat ? 'btn-primary' : 'btn-secondary'}`}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600 ml-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={e => setShowInactive(e.target.checked)}
          />
          Show inactive
        </label>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-error mb-4">
          <strong>Error:</strong> {error}
          <button onClick={loadFeatures} className="btn btn-secondary btn-sm ml-3">Try Again</button>
        </div>
      )}

      {/* Loading */}
      {loading && features.length === 0 ? (
        <div className="text-center py-8">
          <div className="loading-dots mx-auto mb-3">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
          <p className="text-gray-600">Loading features...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-500">No features match the current filters.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(feature => (
            <FeatureRow
              key={feature.key}
              feature={feature}
              onEdit={() => setEditingFeature(feature)}
              onDeactivate={() => setConfirmDeactivate(feature)}
              onReactivate={() => reactivateFeature(feature.key)}
              loading={loading}
            />
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <FeatureModal
          title="Add Feature"
          onClose={() => setShowAddModal(false)}
          onSubmit={createFeature}
          loading={loading}
        />
      )}

      {/* Edit Modal */}
      {editingFeature && (
        <FeatureModal
          title="Edit Feature"
          initial={editingFeature}
          onClose={() => setEditingFeature(null)}
          onSubmit={data => updateFeature(editingFeature.key, data)}
          loading={loading}
          isEdit
        />
      )}

      {/* Deactivate Confirm */}
      {confirmDeactivate && (
        <ConfirmDeactivateModal
          feature={confirmDeactivate}
          onClose={() => setConfirmDeactivate(null)}
          onConfirm={() => deactivateFeature(confirmDeactivate.key)}
          loading={loading}
        />
      )}
    </div>
  );
};

FeatureManagement.propTypes = {
  onBack: PropTypes.func.isRequired,
};

// ─── Feature Row ─────────────────────────────────────────────────────────────

const FeatureRow = ({ feature, onEdit, onDeactivate, onReactivate, loading }) => (
  <div className={`tenant-card ${!feature.is_active ? 'opacity-60' : ''}`}>
    <div className="flex flex-between">
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-1">
          <span className="font-mono text-sm font-semibold text-gray-700">{feature.key}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[feature.category] || 'bg-gray-100 text-gray-600'}`}>
            {feature.category}
          </span>
          {!feature.is_active && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">inactive</span>
          )}
        </div>
        <div className="text-base font-medium text-gray-800 mb-1">{feature.display_name}</div>
        {feature.description && (
          <p className="text-sm text-gray-500 mb-1">{feature.description}</p>
        )}
        <div className="flex gap-4 text-xs text-gray-400 mt-1">
          <span>Default: <strong>{feature.default_enabled ? 'enabled' : 'disabled'}</strong></span>
          {feature.created_at && (
            <span>Created: {new Date(feature.created_at).toLocaleDateString()}</span>
          )}
        </div>
      </div>
      <div className="tenant-actions">
        <button className="btn btn-secondary btn-sm" onClick={onEdit} disabled={loading}>
          Edit
        </button>
        {feature.is_active ? (
          <button className="btn btn-secondary btn-sm" onClick={onDeactivate} disabled={loading}
            style={{ color: 'var(--color-error, #dc2626)' }}>
            Deactivate
          </button>
        ) : (
          <button className="btn btn-secondary btn-sm" onClick={onReactivate} disabled={loading}>
            Reactivate
          </button>
        )}
      </div>
    </div>
  </div>
);

FeatureRow.propTypes = {
  feature: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDeactivate: PropTypes.func.isRequired,
  onReactivate: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

// ─── Feature Modal (Add / Edit) ───────────────────────────────────────────────

const FeatureModal = ({ title, initial, onClose, onSubmit, loading, isEdit }) => {
  const [formData, setFormData] = useState({
    key:             initial?.key || '',
    display_name:    initial?.display_name || '',
    description:     initial?.description || '',
    category:        initial?.category || 'platform',
    default_enabled: initial?.default_enabled ?? false,
    is_active:       initial?.is_active ?? true,
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!isEdit && !formData.key.trim()) errs.key = 'Key is required';
    if (!isEdit && !/^[a-z0-9_]+$/.test(formData.key.trim())) errs.key = 'Key must be lowercase letters, numbers, and underscores only';
    if (!formData.display_name.trim()) errs.display_name = 'Display name is required';
    if (!CATEGORIES.includes(formData.category)) errs.category = 'Invalid category';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = {
      display_name:    formData.display_name.trim(),
      description:     formData.description.trim() || null,
      category:        formData.category,
      default_enabled: formData.default_enabled,
    };
    if (!isEdit) payload.key = formData.key.trim();
    if (isEdit) payload.is_active = formData.is_active;
    await onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" disabled={loading}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Key — only on create */}
          {!isEdit && (
            <div className="mb-4">
              <label className="form-label" htmlFor="key">Key *</label>
              <input
                type="text"
                id="key"
                name="key"
                value={formData.key}
                onChange={handleChange}
                className={`form-input ${errors.key ? 'border-red-500' : ''}`}
                placeholder="e.g., advanced_analytics"
                disabled={loading}
                required
              />
              {errors.key && <p className="text-sm text-red-600 mt-1">{errors.key}</p>}
              <p className="text-xs text-gray-500 mt-1">Lowercase letters, numbers, and underscores only. Cannot be changed after creation.</p>
            </div>
          )}

          {/* Display Name */}
          <div className="mb-4">
            <label className="form-label" htmlFor="display_name">Display Name *</label>
            <input
              type="text"
              id="display_name"
              name="display_name"
              value={formData.display_name}
              onChange={handleChange}
              className={`form-input ${errors.display_name ? 'border-red-500' : ''}`}
              placeholder="e.g., Advanced Analytics"
              disabled={loading}
              required
            />
            {errors.display_name && <p className="text-sm text-red-600 mt-1">{errors.display_name}</p>}
          </div>

          {/* Description */}
          <div className="mb-4">
            <label className="form-label" htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="form-input"
              rows={2}
              placeholder="Optional description of what this feature does"
              disabled={loading}
            />
          </div>

          {/* Category */}
          <div className="mb-4">
            <label className="form-label" htmlFor="category">Category *</label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              className={`form-input ${errors.category ? 'border-red-500' : ''}`}
              disabled={loading}
            >
              {CATEGORIES.map(c => (
                <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
              ))}
            </select>
            {errors.category && <p className="text-sm text-red-600 mt-1">{errors.category}</p>}
          </div>

          {/* Toggles */}
          <div className="mb-6 space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="default_enabled"
                checked={formData.default_enabled}
                onChange={handleChange}
                disabled={loading}
              />
              <span className="text-sm text-gray-700">Enabled by default for new tenants</span>
            </label>
            {isEdit && (
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  disabled={loading}
                />
                <span className="text-sm text-gray-700">Active (visible to tenants)</span>
              </label>
            )}
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary flex-1" disabled={loading}>
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="loading-dots">
                    <div className="loading-dot"></div>
                    <div className="loading-dot"></div>
                    <div className="loading-dot"></div>
                  </div>
                  Saving...
                </span>
              ) : (isEdit ? 'Save Changes' : 'Add Feature')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

FeatureModal.propTypes = {
  title:    PropTypes.string.isRequired,
  initial:  PropTypes.object,
  onClose:  PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading:  PropTypes.bool.isRequired,
  isEdit:   PropTypes.bool,
};

// ─── Confirm Deactivate Modal ────────────────────────────────────────────────

const ConfirmDeactivateModal = ({ feature, onClose, onConfirm, loading }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 w-full max-w-sm mx-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-2">Deactivate Feature</h3>
      <p className="text-gray-600 mb-1">
        Are you sure you want to deactivate <strong>{feature.display_name}</strong>?
      </p>
      <p className="text-sm text-gray-500 mb-6">
        It will no longer be visible to tenants. Existing tenant flag overrides are preserved and the feature can be reactivated later.
      </p>
      <div className="flex gap-3">
        <button onClick={onClose} className="btn btn-secondary flex-1" disabled={loading}>Cancel</button>
        <button
          onClick={onConfirm}
          className="btn btn-primary flex-1"
          disabled={loading}
          style={{ background: 'var(--color-error, #dc2626)', borderColor: 'var(--color-error, #dc2626)' }}
        >
          {loading ? 'Deactivating...' : 'Deactivate'}
        </button>
      </div>
    </div>
  </div>
);

ConfirmDeactivateModal.propTypes = {
  feature:   PropTypes.object.isRequired,
  onClose:   PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  loading:   PropTypes.bool.isRequired,
};

export default FeatureManagement;
